import { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import type { AppliedFlashRoomRate, SubmissionAttemptFailure } from '../types';

import { previewPackageLinks } from './linkPackages';
import {
  getMaxExtraNights,
  getOfferPackageName,
  getPackagePersistId,
  pruneManualRoomTypeIds,
  sortPackagesByLengthOfStay,
} from './utils';
import type { UnmatchedFlashPackage } from './utils';

export interface UsePackageSetupStepParams {
  aiRoomNameOverrides: Record<string, string>;
  appliedFlashRoomRates: Array<AppliedFlashRoomRate>;
  isPackageSetupStep: boolean;
  propertyId: string | undefined;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  salesforceRooms: Array<SalesforceRoom>;
  setIsAdvancingStep: (isAdvancing: boolean) => void;
  trimmedSalesforceOfferId: string;
}

/**
 * Owns the Package Setup step (8): matching applied room rates to Salesforce packages (a
 * preview against draft data -- real assignment happens at Submission), and the flexible-nights
 * toggle a CM sets per package, held as a draft until Submission applies it for real.
 */
export function usePackageSetupStep({
  aiRoomNameOverrides,
  appliedFlashRoomRates,
  isPackageSetupStep,
  propertyId,
  roomTypes,
  salesforceRooms,
  setIsAdvancingStep,
  trimmedSalesforceOfferId,
}: UsePackageSetupStepParams) {
  const { enqueueSnackbar } = useSnackbar();
  const [matchedPackages, setMatchedPackages] = useState<Array<App.Package>>([]);
  const [isLinkingPackages, setIsLinkingPackages] = useState(false);
  const [hasAttemptedPackageLink, setHasAttemptedPackageLink] = useState(false);
  const [matchedRoomRateCount, setMatchedRoomRateCount] = useState(0);
  const [packageLinkFailures, setPackageLinkFailures] = useState<Array<string>>([]);
  const [unmatchedPackages, setUnmatchedPackages] = useState<Array<UnmatchedFlashPackage>>([]);
  const [assignableRoomTypes, setAssignableRoomTypes] = useState<Array<Pick<App.RoomType, 'id' | 'name'>>>([]);
  const [storedManualRoomTypeIds, setStoredManualRoomTypeIds] = useState<Record<string, string>>({});
  const [flexibleNightsById, setFlexibleNightsById] = useState<Record<string, boolean>>({});
  const [confirmedFlexibleNightsCount, setConfirmedFlexibleNightsCount] = useState(0);

  // Derived so a manual pick of a since-removed draft room (or another property's) can't linger.
  const manualPackageRoomTypeIds = pruneManualRoomTypeIds(
    storedManualRoomTypeIds,
    roomTypes.map((roomType) => roomType.id),
  );
  const sortedMatchedPackages = useMemo(() => sortPackagesByLengthOfStay(matchedPackages), [matchedPackages]);

  // Newly matched packages get their Salesforce flexible_nights value seeded once; a package the
  // CM has already toggled here (still matched after a re-link) keeps that edit.
  useEffect(() => {
    setFlexibleNightsById((prev) => {
      let hasNewEntry = false;
      const next = { ...prev };

      matchedPackages.forEach((pkg) => {
        const packageId = getPackagePersistId(pkg);

        if (packageId && !(packageId in next)) {
          next[packageId] = 'flexible_nights' in pkg && !!pkg.flexible_nights;
          hasNewEntry = true;
        }
      });

      return hasNewEntry ? next : prev;
    });
  }, [matchedPackages]);

  const flexibleNightsWithoutMaxNights = matchedPackages.flatMap((pkg) => {
    const packageId = getPackagePersistId(pkg);
    const isFlexibleNights = (packageId && flexibleNightsById[packageId]) ?? false;
    const hasNoMaxNights = getMaxExtraNights(pkg) === 0;
    return packageId && isFlexibleNights && hasNoMaxNights ? [{ packageId, name: getOfferPackageName(pkg) }] : [];
  });
  const flexibleNightsNextStepsCount = flexibleNightsWithoutMaxNights.length || confirmedFlexibleNightsCount;
  const hasUnresolvedFlexibleNightsWarning = flexibleNightsWithoutMaxNights.length > 0;

  const isPackageSetupStepNextDisabled =
    isPackageSetupStep &&
    (!hasAttemptedPackageLink ||
      isLinkingPackages ||
      packageLinkFailures.length > 0 ||
      hasUnresolvedFlexibleNightsWarning);

  async function assignRoomRatesToPackages(): Promise<{
    failures: Array<string>;
    matchedPackages: Array<App.Package>;
  }> {
    if (!propertyId || appliedFlashRoomRates.length === 0) {
      return { failures: [], matchedPackages: [] };
    }

    setIsAdvancingStep(true);

    const {
      assignableRoomTypes: linkedAssignableRoomTypes,
      isFatalError,
      matchedPackages: matched,
      matchedRoomRateCount: linkedCount,
      unmatchedPackageNames,
      unmatchedPackages: linkedUnmatchedPackages,
    } = await previewPackageLinks({
      aiRoomNameOverrides,
      appliedFlashRoomRates,
      manualPackageRoomTypeIds,
      propertyId,
      roomTypes,
      salesforceRooms,
      trimmedSalesforceOfferId,
    });

    setIsAdvancingStep(false);
    setAssignableRoomTypes(linkedAssignableRoomTypes);
    setUnmatchedPackages(linkedUnmatchedPackages);
    setMatchedRoomRateCount(linkedCount);
    setPackageLinkFailures(unmatchedPackageNames);

    if (!isFatalError && unmatchedPackageNames.length > 0) {
      enqueueSnackbar(
        `Unable to match ${unmatchedPackageNames.length} package${unmatchedPackageNames.length === 1 ? '' : 's'} to a room and rate`,
        { variant: 'warning' },
      );
    }

    return { failures: unmatchedPackageNames, matchedPackages: matched };
  }

  async function handleLinkPackages() {
    setHasAttemptedPackageLink(true);
    setIsLinkingPackages(true);

    const { failures, matchedPackages: matched } = await assignRoomRatesToPackages();

    setIsLinkingPackages(false);
    setMatchedPackages(matched);

    return failures;
  }

  // Same readiness check as assignRoomRatesToPackages, so it can't fire early and get stuck
  // permanently no-op'd (hasAttemptedPackageLink locked true with no retry). Includes
  // salesforceRooms since buildFlashPackageRoomRateAssignments needs it for name-matching.
  useEffect(() => {
    if (
      !isPackageSetupStep ||
      hasAttemptedPackageLink ||
      isLinkingPackages ||
      !propertyId ||
      appliedFlashRoomRates.length === 0 ||
      salesforceRooms.length === 0
    ) {
      return;
    }

    void handleLinkPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPackageSetupStep,
    hasAttemptedPackageLink,
    isLinkingPackages,
    propertyId,
    appliedFlashRoomRates.length,
    salesforceRooms.length,
  ]);

  function selectRoomTypeForPackage(packageId: string, roomTypeId: string) {
    setStoredManualRoomTypeIds((current) => ({ ...current, [packageId]: roomTypeId }));
  }

  function toggleFlexibleNights(packageId: string, checked: boolean) {
    setFlexibleNightsById((prev) => ({ ...prev, [packageId]: checked }));
  }

  // Locally resolves an unmatchable flexible-nights package (no Salesforce max-nights value) by
  // turning its toggle off -- applying this for real is the Submission step's job.
  function confirmDisableFlexibleNights() {
    const targets = flexibleNightsWithoutMaxNights;

    if (targets.length === 0) {
      return;
    }

    setFlexibleNightsById((prev) => {
      const next = { ...prev };
      targets.forEach(({ packageId }) => {
        next[packageId] = false;
      });
      return next;
    });
    setConfirmedFlexibleNightsCount(targets.length);
  }

  // Only a room-name mismatch ("Retry linking" re-attempts it) and a max-nights-less flexible
  // package ("Confirm" disables it) are fixable here -- any other failure is a backend rejection.
  function isSubmissionFailureActionable({
    flexibleNightsBlockedCount,
    packageUnmatchedCount,
  }: SubmissionAttemptFailure) {
    return packageUnmatchedCount > 0 || flexibleNightsBlockedCount > 0;
  }

  // A submission attempt can change the package data this step matched against, so clearing the
  // attempted flag lets the auto-link effect re-run and refresh the match on re-entry.
  function resetAfterFailedSubmission() {
    setHasAttemptedPackageLink(false);
    setPackageLinkFailures([]);
    setMatchedRoomRateCount(0);
  }

  function reset() {
    setMatchedPackages([]);
    setIsLinkingPackages(false);
    setHasAttemptedPackageLink(false);
    setMatchedRoomRateCount(0);
    setPackageLinkFailures([]);
    setUnmatchedPackages([]);
    setAssignableRoomTypes([]);
    setStoredManualRoomTypeIds({});
    setFlexibleNightsById({});
    setConfirmedFlexibleNightsCount(0);
  }

  return {
    assignableRoomTypes,
    confirmDisableFlexibleNights,
    flexibleNightsById,
    flexibleNightsNextStepsCount,
    flexibleNightsWithoutMaxNights,
    handleLinkPackages,
    resetAfterFailedSubmission,
    hasAttemptedPackageLink,
    hasUnresolvedFlexibleNightsWarning,
    isLinkingPackages,
    isPackageSetupStepNextDisabled,
    isSubmissionFailureActionable,
    manualPackageRoomTypeIds,
    matchedPackages,
    matchedRoomRateCount,
    packageLinkFailures,
    reset,
    selectRoomTypeForPackage,
    sortedMatchedPackages,
    toggleFlexibleNights,
    unmatchedPackages,
  };
}
